"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Check } from "lucide-react";

const benefits = [
  { title: "Flexible usage, clear scope", description: "No minimum usage and no long term lock in, designed for short let operators, serviced apartments, and hotels." },
  { title: "Consistent standards, predictable outcomes", description: "Get reliable linen and towel supply, with delivery windows you can plan around and reporting that shows what was delivered, when, and to which unit." }
];

export function QualityShowcase() {
  return (
    <div 
      className="w-full py-16 sm:py-20 lg:py-24 bg-white relative z-10"
    >
      <div className="px-4 sm:px-6 xl:px-0 xl:mx-[16.67%]">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
        
          {/* Left Column - Content */}
          <div className="lg:sticky lg:top-32">
            <motion.span 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-3"
            >
              QUALITY
            </motion.span>
          
            <motion.h2 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-xl sm:text-2xl lg:text-3xl font-satoshi font-bold text-gray-900 leading-tight mb-6"
            >
              Linen hire and towel hire, managed in one platform
            </motion.h2>
          
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-4 mb-8"
            >
              <p className="text-base sm:text-lg font-light text-gray-600 leading-relaxed">
                Register on the Replenico platform for on demand linen rental and towel hire, built for property turnover management.
              </p>
              <p className="text-base sm:text-lg font-light text-gray-600 leading-relaxed">
              Upload your properties once, then manage and replenish each unit from your dashboard on mobile, tablet, or desktop. From bed linen hire to towels and verified consumables replenishment, everything is tracked by unit so you stay in control.
            </p>
          </motion.div>

          {/* Benefits List */}
          <div className="space-y-6 mb-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                viewport={{ once: true }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center mt-0.5">
                  <Check className="w-3.5 h-3.5 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-satoshi font-semibold text-gray-800 mb-1">
                    {benefit.title}
                  </h3>
                  <p className="text-base font-light text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Link */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <Link 
              href="/linen-services"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-colors group shadow-lg shadow-teal-600/20"
            >
              <span>Find out more</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        {/* Right Column - Professional Mockup Image - Hidden on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
          className="relative hidden lg:flex items-center justify-center min-h-[500px]"
        >
          <motion.div
            initial={{ y: 0 }}
            whileInView={{ y: -20 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            viewport={{ once: false, margin: "-50px" }}
            className="relative w-full"
          >
            <Image
              src="/assets/REPLENICO/mockups/Mockup_short-term-rentals.png"
              alt="Replenico platform dashboard"
              width={800}
              height={600}
              className="w-full h-auto object-contain"
              sizes="50vw"
              priority
            />
          </motion.div>
        </motion.div>
        </div>
      </div>
    </div>
  );
}
